
import { useCallback } from 'react';

/**
 * Custom hook for tracking analytics events
 */
const useAnalytics = () => {
  /**
   * Track page view in analytics
   */
  const trackPageView = useCallback((path: string, title: string) => {
    try {
      // Check if dataLayer is available (GTM is loaded)
      if (window.dataLayer) {
        // Push page view event to GTM for Plausible
        window.dataLayer.push({
          event: 'pageview',
          page: {
            path,
            title
          }
        });
        
        console.log('Analytics: Page view tracked', { path, title });
      } else {
        console.warn('Analytics: dataLayer not found. Page view not tracked.');
      }
    } catch (error) {
      console.error('Analytics: Error tracking page view', error);
    }
  }, []);

  /**
   * Track custom event in analytics
   */
  const trackEvent = useCallback((eventName: string, props?: Record<string, any>) => {
    try {
      // Check if dataLayer is available (GTM is loaded)
      if (window.dataLayer) {
        // Push custom event to GTM for Plausible
        window.dataLayer.push({
          event: eventName,
          ...(props || {})
        });
        
        console.log('Analytics: Event tracked', { eventName, props });
      } else {
        console.warn('Analytics: dataLayer not found. Event not tracked.');
      }
    } catch (error) {
      console.error('Analytics: Error tracking event', error);
    }
  }, []);

  /**
   * Track app view in analytics
   * Specialized event for tracking when a specific app/tool is viewed
   */
  const trackAppView = useCallback((appId: string, appName: string, category: string) => {
    trackEvent('app_view', {
      app_id: appId,
      app_name: appName,
      category: category
    });
  }, [trackEvent]);

  return {
    trackPageView,
    trackEvent,
    trackAppView
  };
};

export default useAnalytics;
